import React, { useCallback, useEffect, useRef } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { DSFlex, DSRangeSlider, DSSpacer, DSSurface, DSText } from '@zoe/ds-web'
import { ContextAwareText } from '../text/ContextAwareText'
import { FieldError } from './FieldError'

// Locally override DSRangeSlider visuals so the design choices for the symptom
// score sliders don't leak to other consumers of the DS tokens:
// - active gradient (aubergine-bold) → white, so the touched fill blends into
//   the white track and only the thumb signals position
// - inactive dots/track (neutral-disabled) → #6f7273
// - dots on the track (text-secondary-inverse) → same #6f7273
// - thicker track (12px → 16px) with the dot overlay and thumb recentred to match
// - thumb explicitly yellow-bold (since we've changed aubergine-bold to white
//   above, which the thumb also reads from) and no drop-shadow
const SliderOverrides = styled.div<{ $activeIndex: number }>`
  --color-surface-aubergine-bold: var(--colour-surface-white);
  --color-surface-neutral-disabled: #6f7273;
  --color-text-secondary-inverse: #6f7273;

  /* Hide the input's own track entirely and stack it above the dot overlay
     so the yellow thumb always paints on top of every dot. The DS rules use
     class-selector specificity, so we use !important to win. Each pseudo
     gets its own rule (browsers drop the whole grouped rule if any selector
     in a comma list has an unrecognised vendor pseudo). */
  input[type='range'] {
    height: 16px !important;
    background: transparent !important;
    z-index: 3;
    position: relative;
  }
  input[type='range']::-webkit-slider-runnable-track {
    height: 16px !important;
    background: transparent !important;
  }
  input[type='range']::-moz-range-track {
    height: 16px !important;
    background: transparent !important;
  }
  /* The dot-row overlay is a sibling div with an inline background gradient.
     Force its background to a solid white so the slider track reads as a
     continuous white strip regardless of touched state, match the new 16px
     height, and centre the dots vertically on it. */
  > div > div[style*='background'] {
    height: 16px;
    align-items: center;
    background: var(--colour-surface-white) !important;
  }
  > div > div[style*='background'] > div {
    margin: 0 4px;
  }
  /* Highlight the dot at the current slider position so the active value reads
     clearly behind the yellow thumb. */
  > div > div[style*='background'] > div:nth-child(${({ $activeIndex }) => $activeIndex + 1}) {
    background-color: var(--colour-surface-yellow-bold);
  }
  /* The untouched centre indicator is the StyledSliderWrapper's other direct
     child (its outer border defaults to a different colour than the inner
     fill, which reads as a dot-in-a-ring). Match the border to the fill so
     the whole ball is one solid grey. */
  > div > div:not([style*='background']) {
    border-color: var(--color-text-secondary-inverse);
  }
  input[type='range']::-webkit-slider-thumb {
    background: var(--colour-surface-yellow-bold) !important;
    box-shadow: none !important;
    top: -4px !important;
  }
  input[type='range']::-moz-range-thumb {
    background: var(--colour-surface-yellow-bold) !important;
    box-shadow: none !important;
  }
`

export interface SliderProps extends React.ComponentProps<'input'> {
  label: string
  sliderLabels: string[]
  name: string
  values: string[]
}

const MemoizedRangeSlider = React.memo(DSRangeSlider)

export const Slider: React.FC<SliderProps> = ({ ...props }) => {
  const { field, fieldState } = useController({ name: props.name })
  const { formState } = useFormContext()
  const fieldRef = useRef(field)
  fieldRef.current = field
  const wrapperRef = useRef<HTMLDivElement>(null)

  const onChangeValidation = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rounded = Math.round(parseFloat(e.target.value))
    fieldRef.current.onChange(rounded.toString())
  }, [])

  const max = props.values.length - 1

  // DSRangeSlider is uncontrolled — its internal state starts at `min` and stays
  // "untouched" (greyed dots, hidden thumb) until the user pointer-downs on it.
  // For form values restored from the quiz state on revisit, replay the events
  // imperatively on mount so the slider visually reflects the saved answer:
  //   1. pointerdown → marks the DS slider as touched (dots + thumb appear)
  //   2. native-setter + change → DS slider's onChange picks up our value and
  //      syncs its internal value state.
  // The synthetic change the pointerdown triggers transiently writes the slider's
  // current internal value (0) into the form; the subsequent change event with
  // the restored value puts it back.
  useEffect(() => {
    const restored = fieldRef.current.value
    if (restored === undefined || restored === '') return
    const input = wrapperRef.current?.querySelector('input[type="range"]') as HTMLInputElement | null
    if (!input) return
    input.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }))
    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
    nativeSetter?.call(input, String(restored))
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }, [])

  const shouldDisplayError = fieldState.error && formState.submitCount > 0
  return (
    <DSFlex direction="column" gap={24}>
      <DSSurface
        surfaceColor="cream-bold"
        radius={8}
        padding={16}
        borderColor={shouldDisplayError ? 'error' : undefined}
      >
        <DSText variant="fluid-paragraph-300" weight="semiBold">
          <ContextAwareText text={props.label} />
        </DSText>
        <DSSpacer size={16} direction="vertical" />
        <SliderOverrides
          ref={wrapperRef}
          $activeIndex={String(field.value ?? '') === '' ? -1 : parseInt(String(field.value), 10)}
        >
          <MemoizedRangeSlider name={props.name} step={1} min={0} max={max} onChange={onChangeValidation} />
        </SliderOverrides>
        <DSSpacer size={16} direction="vertical" />
        {props.sliderLabels && (
          <>
            <DSSpacer size={8} direction="vertical" />
            <DSFlex direction="row" justifyContent="space-between">
              {props.sliderLabels.map((label, index) => (
                <DSText key={index} variant="fluid-label-100">
                  {label}
                </DSText>
              ))}
            </DSFlex>
          </>
        )}
        {shouldDisplayError && (
          <>
            <DSSpacer size={16} direction="vertical" />
            <FieldError>{fieldState.error?.message}</FieldError>
          </>
        )}
      </DSSurface>
    </DSFlex>
  )
}
